import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useReportListScroll from "../hooks/useReportListScroll.js";
import { Badge, Button, Col, Dropdown, Form, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCog, faDownload, faFileLines, faRotate } from "@fortawesome/free-solid-svg-icons";
import { formatInTimeZone } from "date-fns-tz";
import { confirmAlert } from "react-confirm-alert";
import NotyfContext from "../contexts/NotyfContext.js";
import activityPhotoCompetitionImage from "../assets/img/activity-photo-competition.png";
import activityPhotoSubmissionImage from "../assets/img/activity-photo-submission.png";
import activityQuizImage from "../assets/img/activity-quiz.png";
import activityVotingImage from "../assets/img/activity-voting.png";
import { ShowConfirm } from "./Alerts.js";
import Loader from "./Loader.js";
import MediaPreviewModal from "./MediaPreviewModal.js";
import { ColumnSortingTable } from "./Table.js";
import ActivityReport, { getVoteDetailColumns, prepareVoteDetailRows } from "./apps/ActivityReport.js";
import { PHOTO_COMPETITION_ACTIVITY_ID, PHOTO_SUBMISSION_ACTIVITY_ID } from "./apps/ActivityPhotoForm.js";
import { PARTICIPANT_VOTING_ACTIVITY_ID } from "./apps/ActivityVotingForm.js";
import { getActivityDisplayName, getActivityStatusBadgeClass, getActivityStatusText } from "./apps/ActivityTableUtils.js";
import { getDownloadFileName } from "../utils/downloadFilename.js";

const REPORT_TIME_ZONE = "America/Sao_Paulo";

const formatActivityDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : formatInTimeZone(date, REPORT_TIME_ZONE, "dd/MM/yyyy HH:mm");
};

export const normalizeActivityReportSummary = (activity = {}) => {
  const status = Number(activity.status ?? 0);
  const statusBadgeClass = getActivityStatusBadgeClass(status, activity);
  return {
    ...activity,
    type_label: getActivityDisplayName(activity),
    status_label: getActivityStatusText(status, activity),
    status_badge_class: statusBadgeClass,
    status_variant: statusBadgeClass.replace("bg-", ""),
    creation_date: activity.created_at || activity.creation_date || activity.data_criacao || activity.json?.created_at || "",
  };
};

export const getActivityReportImage = (appId) => ({
  1: activityQuizImage,
  [PHOTO_SUBMISSION_ACTIVITY_ID]: activityPhotoSubmissionImage,
  [PHOTO_COMPETITION_ACTIVITY_ID]: activityPhotoCompetitionImage,
  [PARTICIPANT_VOTING_ACTIVITY_ID]: activityVotingImage,
}[Number(appId)] || activityQuizImage);

const ActivityAvailabilityBadges = ({ activity }) => {
  const availability = activity?.json?.availability || {};
  if (availability.status !== true) return null;
  return (
    <>
      {availability.start_at && <Badge bg="light" text="dark" className="border">Início: {formatActivityDate(availability.start_at)}</Badge>}
      {availability.end_at && <Badge bg="light" text="dark" className="border">Fim: {formatActivityDate(availability.end_at)}</Badge>}
    </>
  );
};

export const getActivityReportScoreId = (activity) => {
  const json = activity?.json || {};
  return [PHOTO_COMPETITION_ACTIVITY_ID, PARTICIPANT_VOTING_ACTIVITY_ID].includes(Number(activity?.app_id))
    ? json.winner_score_id : json.score_id;
};

export const getActivityReportDeleteTargetId = (activity, row = {}) => {
  if (Number(activity?.app_id) === 1 || Number(activity?.app_id) === PARTICIPANT_VOTING_ACTIVITY_ID) {
    return Number(row.user_id) || null;
  }
  if ([PHOTO_SUBMISSION_ACTIVITY_ID, PHOTO_COMPETITION_ACTIVITY_ID].includes(Number(activity?.app_id))) {
    return Number(row.submission_id) || null;
  }
  return null;
};

const getDeleteDescription = (activity, scope) => {
  if (scope === "all") {
    return "Todos os dados desta atividade serão excluídos. A configuração da atividade será preservada.";
  }
  if (Number(activity?.app_id) === 1) return "O resultado deste participante será excluído.";
  if (Number(activity?.app_id) === PHOTO_SUBMISSION_ACTIVITY_ID) return "O envio, a foto e a pontuação vinculada serão excluídos.";
  if (Number(activity?.app_id) === PHOTO_COMPETITION_ACTIVITY_ID) return "O envio, a foto e os votos vinculados serão excluídos.";
  return "Todos os votos recebidos por este participante serão excluídos. O participante continuará na lista com zero votos.";
};

const VoteDetailsModal = ({ activity, targetRow, dataTable, onHide, onOpenMedia }) => {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [triggerExport, setTriggerExport] = useState(false);
  const requestRef = useRef(null);
  const targetId = Number(activity?.app_id) === PHOTO_COMPETITION_ACTIVITY_ID ? targetRow?.submission_id : targetRow?.user_id;

  useEffect(() => {
    if (!activity?.id || !targetId) return undefined;
    requestRef.current?.cancel("vote-details-reload");
    const source = axios.CancelToken.source();
    requestRef.current = source;
    setReport(null);
    setError("");
    axios.get("/api/event/plataformaEventos/activity-report/votes", {
      params: { event_id: dataTable.event_id, activity_id: activity.id, target_id: targetId },
      cancelToken: source.token,
    }).then((response) => setReport(response.data)).catch((requestError) => {
      if (!axios.isCancel(requestError)) setError(requestError.response?.data?.message || "Não foi possível carregar os votos.");
    });
    return () => source.cancel("vote-details-unmount");
  }, [activity?.id, dataTable.event_id, reload, targetId]);

  const rows = useMemo(() => prepareVoteDetailRows(report?.votes, dataTable), [dataTable, report?.votes]);
  const columns = useMemo(() => getVoteDetailColumns(dataTable, true), [dataTable]);

  return (
    <Modal show={Boolean(targetRow)} onHide={onHide} size="xl" centered className="activity-vote-details-modal" backdropClassName="activity-vote-details-backdrop">
      <Modal.Header closeButton>
        <Modal.Title>
          <span className="d-flex align-items-center gap-2">
            {targetRow?.photo_url && <button type="button" className="btn p-0 border-0" onClick={() => onOpenMedia?.(targetRow.photo_url, targetRow)}><img src={targetRow.photo_url} alt="" className="activity-vote-target-image" /></button>}
            <span>Detalhes dos votos<br /><small className="text-muted">{targetRow?.participant_name || targetRow?.email || "Participante"} · {report?.total_votes ?? targetRow?.vote_count ?? 0} votos</small></span>
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-end gap-2 mb-3">
          <Button variant="outline-secondary" size="sm" onClick={() => setReload((value) => value + 1)}><FontAwesomeIcon icon={faRotate} className="me-1" />Atualizar</Button>
          <Button variant="primary" size="sm" onClick={() => setTriggerExport(true)} disabled={!rows.length}>Exportar</Button>
        </div>
        {!report && !error && <div className="py-5"><Loader /></div>}
        {error && <div className="text-center text-danger py-5"><p>{error}</p><Button size="sm" onClick={() => setReload((value) => value + 1)}>Tentar novamente</Button></div>}
        {report && !rows.length && <div className="text-center text-muted py-5">{report.snapshot_only ? "O total final está congelado no resultado. As identidades individuais dos votos não fazem parte do snapshot." : "Nenhum voto registrado."}</div>}
        {report && rows.length > 0 && (
          <ColumnSortingTable columns={columns} data={rows} title={`${dataTable.title} - votos - ${targetRow?.participant_name || targetId}`} removeCard triggerExport={triggerExport} setTriggerExport={setTriggerExport} />
        )}
      </Modal.Body>
    </Modal>
  );
};

export const ActivityReportModal = ({ dataTable, activity, show, onHide, canEdit }) => {
  const notyf = useContext(NotyfContext);
  const [activityReport, setActivityReport] = useState(null);
  const [reportError, setReportError] = useState("");
  const [reportReload, setReportReload] = useState(0);
  const [showDetails, setShowDetails] = useState(true);
  const [triggerExport, setTriggerExport] = useState(false);
  const [voteTarget, setVoteTarget] = useState(null);
  const [media, setMedia] = useState({ show: false, url: "" });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!show || !activity?.id) {
      setActivityReport(null);
      setReportError("");
      setVoteTarget(null);
      setTriggerExport(false);
      setShowDetails(true);
      return undefined;
    }

    const source = axios.CancelToken.source();
    setActivityReport(null);
    setReportError("");
    axios.get("/api/event/plataformaEventos/activity-report", {
      params: { event_id: dataTable.event_id, activity_id: activity.id },
      cancelToken: source.token,
    }).then((response) => setActivityReport(normalizeActivityReportSummary(response.data.activity))).catch((error) => {
      if (!axios.isCancel(error)) setReportError(error.response?.data?.message || "Não foi possível carregar o relatório.");
    });
    return () => source.cancel("activity-report-unmount");
  }, [activity?.id, dataTable.event_id, reportReload, show]);

  const closeReport = () => {
    setActivityReport(null);
    setVoteTarget(null);
    setTriggerExport(false);
    setShowDetails(true);
    onHide?.();
  };

  const deleteActivityData = (scope, row = null) => {
    if (!canEdit || !activityReport?.id || deleting) return;
    const targetId = scope === "single" ? getActivityReportDeleteTargetId(activityReport, row) : null;
    if (scope === "single" && !targetId) {
      notyf.open({ type: "danger", message: "Não foi possível identificar os dados selecionados.", ripple: true, dismissible: true });
      return;
    }
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Confirmar exclusão"
          description={getDeleteDescription(activityReport, scope)}
          textConfirm="Excluir"
          action={() => {
            setDeleting(true);
            axios.delete("/api/event/plataformaEventos/activity-report", {
              params: {
                event_id: dataTable.event_id,
                activity_id: activityReport.id,
                scope,
                ...(targetId ? { target_id: targetId } : {}),
              },
            }).then((response) => {
              notyf.open({
                type: response.data?.media_cleanup_pending ? "warning" : "success",
                message: response.data?.media_cleanup_pending
                  ? "Dados excluídos, mas alguns arquivos ainda precisam de limpeza."
                  : "Dados da atividade excluídos com sucesso.",
                ripple: true,
                dismissible: true,
              });
              setVoteTarget(null);
              setReportReload((value) => value + 1);
            }).catch((error) => notyf.open({
              type: "danger",
              message: error.response?.data?.message || "Não foi possível excluir os dados da atividade.",
              ripple: true,
              dismissible: true,
            })).finally(() => setDeleting(false));
          }}
          onClose={onClose}
        />
      ),
    });
  };

  return (
    <>
      <Modal show={show && Boolean(activity)} onHide={closeReport} fullscreen className="activity-report-modal">
        <Modal.Header closeButton>
          <div className="activity-report-modal-heading">
            <img
              className="activity-report-modal-image"
              src={getActivityReportImage(activityReport?.app_id ?? activity?.app_id)}
              alt=""
            />
            <div className="flex-grow-1 pe-3">
              <Modal.Title>{activityReport?.title || activity?.title || "Relatório da atividade"}</Modal.Title>
              <small className="text-muted d-block">{activityReport?.type_label || activity?.type_label || getActivityDisplayName(activity)}</small>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-end mb-3">
            <Dropdown>
              <Dropdown.Toggle variant="primary"><FontAwesomeIcon icon={faCog} /></Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => setTriggerExport(true)} disabled={!activityReport || !activityReport.history?.length}>Exportar</Dropdown.Item>
                <Dropdown.Item onClick={() => setShowDetails((value) => !value)}>{showDetails ? "Esconder informações adicionais" : "Mostrar informações adicionais"}</Dropdown.Item>
                {canEdit && <Dropdown.Item disabled={!activityReport || deleting} onClick={() => deleteActivityData("all")}>Excluir todos os dados da atividade</Dropdown.Item>}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          {!activityReport && !reportError && <div className="py-5"><Loader /></div>}
          {reportError && <div className="text-center text-danger py-5"><p>{reportError}</p><Button onClick={() => setReportReload((value) => value + 1)}>Tentar novamente</Button></div>}
          {activityReport && (
            <ActivityReport
              activity={activityReport}
              dataTable={dataTable}
              showDetails={showDetails}
              triggerExport={triggerExport}
              setTriggerExport={setTriggerExport}
              onDeleteResult={canEdit ? (row) => deleteActivityData("single", row) : null}
              onOpenMedia={(url, row) => setMedia({ show: true, url, alt: row?.participant_name || "Foto da atividade" })}
              onOpenVoteDetails={setVoteTarget}
            />
          )}
        </Modal.Body>
      </Modal>

      {activityReport && voteTarget && <VoteDetailsModal activity={activityReport} targetRow={voteTarget} dataTable={dataTable} onHide={() => setVoteTarget(null)} onOpenMedia={(url, row) => setMedia({ show: true, url, alt: row?.participant_name || "Foto da atividade" })} />}
      <MediaPreviewModal media={media} setMedia={setMedia} />
    </>
  );
};

const ActivityReportsSection = ({ dataTable, canEdit, refreshTrigger, onLoadingChange }) => {
  const notyf = useContext(NotyfContext);
  const [activities, setActivities] = useState(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const maxHeight = useReportListScroll(containerRef, listRef, activities);
  const [listError, setListError] = useState("");
  const [selected, setSelected] = useState(null);
  const [deletingAllHistory, setDeletingAllHistory] = useState(false);
  const [downloadingActivityId, setDownloadingActivityId] = useState(null);

  const loadActivities = useCallback(() => {
    onLoadingChange?.("activities", true);
    setListError("");
    axios.get("/api/event/plataformaEventos/component", { params: { event_id: dataTable.event_id, component_type: "atividades" } })
      .then((response) => {
        setActivities((response.data.message || []).map(normalizeActivityReportSummary));
      })
      .catch((error) => {
        setActivities([]);
        setListError(error.response?.data?.message || "Não foi possível carregar as atividades.");
      })
      .finally(() => onLoadingChange?.("activities", false));
  }, [dataTable.event_id, onLoadingChange]);

  useEffect(() => { loadActivities(); }, [loadActivities, refreshTrigger]);

  const deleteAllActivityHistory = () => {
    if (!canEdit || deletingAllHistory) return;
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => (
        <ShowConfirm
          title="Excluir histórico das atividades"
          description="Todos os resultados, envios, fotos e votos das atividades deste evento serão excluídos. As configurações das atividades serão preservadas."
          textConfirm="Excluir histórico"
          action={() => {
            setDeletingAllHistory(true);
            axios.delete("/api/event/plataformaEventos/activity-reports", {
              params: { event_id: dataTable.event_id },
            }).then((response) => {
              notyf.open({
                type: response.data?.media_cleanup_pending ? "warning" : "success",
                message: response.data?.media_cleanup_pending
                  ? "Histórico excluído, mas alguns arquivos ainda precisam de limpeza."
                  : "Histórico de todas as atividades excluído com sucesso.",
                ripple: true,
                dismissible: true,
              });
              loadActivities();
            }).catch((error) => notyf.open({
              type: "danger",
              message: error.response?.data?.message || "Não foi possível excluir o histórico das atividades.",
              ripple: true,
              dismissible: true,
            })).finally(() => setDeletingAllHistory(false));
          }}
          onClose={onClose}
        />
      ),
    });
  };

  const downloadActivityImages = async (activity) => {
    const activityId = Number(activity?.id);
    if (!activityId || downloadingActivityId) return;
    setDownloadingActivityId(activityId);
    const toastId = notyf.open({
      type: "success",
      message: "Gerando o ZIP das imagens da atividade...",
      ripple: true,
      dismissible: false,
      duration: 0,
    });

    try {
      const response = await axios.get("/api/event/plataformaEventos/activity-report/images.zip", {
        params: { event_id: dataTable.event_id, activity_id: activityId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = getDownloadFileName(
        response.headers?.["content-disposition"],
        `atividade-${activityId}-fotos.zip`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      notyf.dismiss(toastId);
      notyf.open({
        type: "success",
        message: "Download das imagens da atividade concluído.",
        ripple: true,
        dismissible: true,
      });
    } catch (error) {
      let message = error.response?.data?.message || "Não foi possível baixar as imagens da atividade.";
      if (error.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await error.response.data.text());
          message = body.message || message;
        } catch (parseError) {
          // Keep the generic download error when the response is not JSON.
        }
      }
      notyf.dismiss(toastId);
      notyf.open({ type: "danger", message, ripple: true, dismissible: true });
    } finally {
      setDownloadingActivityId(null);
    }
  };

  return (
    <section className="activity-reports-section mb-4">
      <Form.Group as={Col} className="textHeaderAplicativo d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Histórico de atividades ({activities === null ? "…" : activities.length})</h5>
        {canEdit && <Dropdown className="d-inline">
          <Dropdown.Toggle className="btn-outline-primary" title="Ações do relatório" aria-label="Ações do relatório">
            <FontAwesomeIcon icon={faCog} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item disabled={deletingAllHistory || !activities?.length} onClick={deleteAllActivityHistory}>
              Excluir o histórico de todas as atividades
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>}
      </Form.Group>
      <h6 className="text-muted mt-3 mx-2">Selecione uma atividade para consultar respostas, envios e votos.</h6>
      <div ref={containerRef} className="border rounded p-3 mb-3 event-report-list-scroll" style={{ maxHeight }}
        role="region" aria-label="Atividades do evento" tabIndex={activities?.length > 10 ? 0 : undefined}>
      {activities === null && <div className="py-5"><Loader /></div>}
      {listError && <div className="text-center text-danger py-5"><p>{listError}</p><Button size="sm" onClick={loadActivities}>Tentar novamente</Button></div>}
      {activities?.length === 0 && !listError && <div className="text-center text-muted py-5">Nenhuma atividade configurada.</div>}
      {activities?.length > 0 && (
        <div ref={listRef} className="list-group">
          {activities.map((activity) => (
            <div key={activity.id} className="list-group-item list-group-item-action mb-2" aria-current="true">
              <div className="d-flex w-100">
                <img className="activity-report-list-image me-3" src={getActivityReportImage(activity.app_id)} alt="" />
                <div className="flex-grow-1">
                  <div className="d-flex w-100 justify-content-between align-items-start gap-3">
                    <div>
                      <h5 className="mb-0 me-2">{activity.title || <span className="text-muted">Sem título</span>}</h5>
                      <small className="text-muted">{activity.type_label}</small>
                    </div>
                  </div>
                  <div className="d-flex w-100 justify-content-between gap-3 mt-auto">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Badge className={activity.status_badge_class}>{activity.status_label}</Badge>
                      <ActivityAvailabilityBadges activity={activity} />
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      {[PHOTO_SUBMISSION_ACTIVITY_ID, PHOTO_COMPETITION_ACTIVITY_ID].includes(Number(activity.app_id)) && (
                        <OverlayTrigger placement="top" overlay={<Tooltip>Baixar imagens</Tooltip>}>
                          <span className="d-inline-block">
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              aria-label="Baixar imagens"
                              disabled={downloadingActivityId === Number(activity.id)}
                              onClick={() => downloadActivityImages(activity)}
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </button>
                          </span>
                        </OverlayTrigger>
                      )}
                      <OverlayTrigger placement="top" overlay={<Tooltip>Abrir relatório</Tooltip>}>
                        <span className="d-inline-block">
                          <button type="button" className="btn btn-sm btn-primary" aria-label="Abrir relatório" onClick={() => setSelected(activity)}>
                            <FontAwesomeIcon icon={faFileLines} />
                          </button>
                        </span>
                      </OverlayTrigger>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      <ActivityReportModal
        dataTable={dataTable}
        activity={selected}
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        canEdit={canEdit}
      />
    </section>
  );
};

export default ActivityReportsSection;
